<script setup>
import {computed, ref, watch} from "vue";
import { useRoute, useRouter } from "vue-router";
import KSAPrimaryButton from "../../../../../components/ui-components/button/KSAPrimaryButton.vue";
import KSASecondaryButton from "../../../../../components/ui-components/button/KSASecondaryButton.vue";
import { useAlertStore } from "../../../../../stores/alertStore";
import adminCorpInfoService from "@/pages/admin/services/corporation/adminCorpInfoService";
import CorpMasterModal from "@/pages/admin/components/modal/CorpMasterModal.vue";
const {$alert, $confirm} = useAlertStore();
const route = useRoute();
const router = useRouter();

const props = defineProps({
  corporation: {
    type: Object,
    default: () => ({}),
  },
});
const emit = defineEmits(['refresh']);

const loaded = ref(false);

const corporation = computed(() => props.corporation || {});

const corpMasterModal = ref(null); // 마스터 지정 모달

const corpInfoYn = ref(false); // 기업정보등록 유무 - 기본 정보 tab
const masterMemberYn = ref(false); // 마스터회원등록 유무 - 기본 정보 tab
const requestedCorpId = ref('');

const setUpdateMode = (enabled) => {
  const nextQuery = { ...route.query };

  if (enabled) {
    nextQuery.mode = 'update';
  } else {
    delete nextQuery.mode;
  }

  router.replace({ query: nextQuery });
};

const retrieveCorpInfoYnAndMasterMemberYnByCorpId = async (corpId) => {
  loaded.value = false;
  corpInfoYn.value = false;
  masterMemberYn.value = false;
  //loadingStore.loading = true;
  adminCorpInfoService.retrieveCorpInfoYnAndMasterMemberYnByCorpId(corpId)
      .then( async (res) => {
        //loadingStore.loading = false;
        loaded.value = true;
        // console.log(res);
        if(res.data.code.toLowerCase() === 'success') {
          const result = res.data.result;
          if (result.corpInfoYn === 'Y') {
            corpInfoYn.value = true;
          }
          if (result.masterMemberYn === 'Y') {
            masterMemberYn.value = true;
          }
        } else {
        }
      }).catch( (res) =>  {
    console.log(res);
    //loadingStore.loading = false;
    loaded.value = true;
  });
}

const onClickCreate = async (corpId) => {
  if (!masterMemberYn.value) {
    const ok = await $confirm(["기업정보를 등록하려면 재직자 중","1명을 지정하여 마스터 권한을 부여해야 합니다."], "마스터 지정");
    if (ok) {
      corpMasterModal.value.init(corpId)
      corpMasterModal.value.show();
    }
  } else {
    router.push({
      name: 'CorporationInfoUpdate',
      params: { id: corpId },
      query: { mode: 'create' },
    });
  }
};

const onClickUpdate = async (corpId) => {
  router.push({name: 'CorporationInfoUpdate', params: {id: corpId}, query: { mode: 'update' }})
};

const getFullUrl = (url) => {
  if (!url) return '#' // url이 없으면 기본 값
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return 'https://' + url
  }
  return url
}

const getYoutubeEmbedUrl = (url) => {
  if (!url) return ''

  const fullUrl = getFullUrl(url)

  try {
    const parsedUrl = new URL(fullUrl)
    const videoId = parsedUrl.searchParams.get('v')

    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}`
    }

    if (parsedUrl.hostname.includes('youtu.be')) {
      return `https://www.youtube.com/embed/${parsedUrl.pathname.replace('/', '')}`
    }

    if (parsedUrl.pathname.includes('/embed/')) {
      return fullUrl
    }
  } catch (error) {
    return ''
  }

  return ''
}

watch(
  () => corporation.value.corpId,
  (corpId) => {
    const normalizedCorpId = corpId ? String(corpId) : '';

    if (!normalizedCorpId || requestedCorpId.value === normalizedCorpId) {
      return;
    }

    requestedCorpId.value = normalizedCorpId;
    retrieveCorpInfoYnAndMasterMemberYnByCorpId(normalizedCorpId);
  },
  { immediate: true }
)

</script>
<template>
  <div class="table-form">
    <table class="vertical">
      <colgroup>
        <col width="200px" />
        <col />
        <col width="200px" />
        <col />
      </colgroup>
      <tr>
        <th>최초 등록일</th>
        <td>{{ $yyyymmddHHmmss(corporation.createDate) }}</td>
        <th>최근 수정일</th>
        <td>{{ $yyyymmddHHmmss(corporation.modifyDate) }}</td>
      </tr>
      <tr>
        <th>로고이미지(CI)</th>
        <td colspan="3">
          <div v-if="corporation.ciImgDir" style="width: 160px; height: 160px;">
            <KSAImage :src="corporation.ciImgDir"/>
          </div>
        </td>
      </tr>
      <tr>
        <th>사업자등록번호</th>
        <td>{{ $bizNum(corporation.bizNum) }}</td>
        <th>업종</th>
        <td>{{ corporation.bizType }}</td>
      </tr>
      <tr>
        <th>기업명</th>
        <td>{{ corporation.corpName }}</td>
        <th>산업군</th>
        <td>{{ $gbNameKR('INDUSTRY_GB', corporation.industryGb) }}</td>
      </tr>
      <tr>
        <th>대표자명</th>
        <td>{{ corporation.ceoName }}</td>
        <th>기업형태</th>
        <td>{{ $gbNameKR('CORP_GB', corporation.corpGb) }}</td>
      </tr>
      <tr>
        <th>창립연월</th>
        <td>{{ $yyyymm(corporation.foundYm) }}</td>
        <th>상장여부</th>
        <td>{{ corporation.corpPublicYn === 'Y' ? '상장' : '비상장' }}</td>
      </tr>
      <tr>
        <th>사원수</th>
        <td>{{ $gbNameKR('EMPLOYEE_NUM_GB', corporation.employeeNumGb) }}</td>
        <th>매출액</th>
        <td>{{ $gbNameKR('SALES_AMT_GB', corporation.salesAmtGb) }}</td>
      </tr>
      <tr>
        <th>사업장소재지</th>
        <td colspan="3">{{ corporation.residenceArea1 }} {{ corporation.residenceArea2 }}</td>
      </tr>
      <tr>
        <th>홈페이지 URL</th>
        <td colspan="3">{{ corporation.homeUrl }}</td>
      </tr>
      <tr>
        <th>사업자등록증</th>
        <td colspan="3">
          <KSATextButton v-if="corporation.bizLicenseDir" class="ml-xs" :text=corporation.bizLicenseName @click="$downloadForPrivate({fileName:corporation.bizLicenseName, fileDir: corporation.bizLicenseDir})" />
        </td>
      </tr>
      <tr>
        <th>기업 소개서</th>
        <td colspan="3">
          <KSATextButton v-if="corporation.corpIntroDir" class="ml-xs" :text=corporation.corpIntroName @click="$downloadForPrivate({fileName:corporation.corpIntroName, fileDir: corporation.corpIntroDir})" />
        </td>
      </tr>
    </table>
  </div>
  <br>
  <div class="table-form word-break-all">
    <table class="vertical">
      <colgroup>
        <col width="200px" />
        <col />
      </colgroup>
      <tr>
        <th>기업 소개 영상</th>
        <td colspan="3">
          <div v-if="corporation.introMovDir || corporation.introYoutubeUrl" class="intro-video">
            <div v-if="corporation.introMovGb === '02' && getYoutubeEmbedUrl(corporation.introYoutubeUrl)" class="video-frame">
              <iframe
                  :src="getYoutubeEmbedUrl(corporation.introYoutubeUrl)"
                  title="기업 소개 영상"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowfullscreen
              />
            </div>
            <div v-else-if="corporation.introMovDir" class="video-frame">
              <video controls preload="metadata">
                <source :src="corporation.introMovDir" />
                브라우저가 동영상 재생을 지원하지 않습니다.
              </video>
            </div>
          </div>
        </td>
      </tr>
      <tr>
        <th>기업소개</th>
        <td colspan="3">
          <div class="board-detail">
            <div class="body">
              <div class="text" v-html="corporation.corpIntro"></div>
            </div>
          </div>
        </td>
      </tr>
      <tr>
        <th>주요 연혁</th>
        <td colspan="3">
          <div class="board-detail">
            <div class="body">
              <div class="text" v-html="corporation.corpHistory"></div>
            </div>
          </div>
        </td>
      </tr>
    </table>
  </div>
  <div class="udt-button-wrapper">
    <KSASecondaryButton v-if="corpInfoYn && masterMemberYn"  class="gray_button" text="수정" @click="onClickUpdate(corporation.corpId)" />
    <KSAPrimaryButton v-else text="기업정보 등록하기" @click="onClickCreate(corporation.corpId)" />
  </div>
  <CorpMasterModal ref="corpMasterModal" /> <!-- 마스터 지정 -->
</template>
<style lang="scss" scoped>
@import "../../../../../assets/scss/theme";

.udt-button-wrapper{
  text-align: right;
  margin-top: 20px;
}
.board-detail {
  * {
    width: 100%;
  }
  .body {
    border-bottom: 0;
    .text {
      padding: 0;
    }
  }
}

.intro-video {
  .video-frame {
    width: 100%;
    max-width: 800px;
    aspect-ratio: 16 / 9;
    background: #000;
    overflow: hidden;
    margin: 0 auto;
  }

  iframe,
  video {
    width: 100%;
    height: 100%;
    border: 0;
    display: block;
  }
}
</style>
